import { Injectable, computed, signal } from '@angular/core';

import type { User } from './auth.types';

/**
 * Estado de autenticação do app — signals reativos.
 *
 * - `sessionToken`: JWT em **memória apenas**. NUNCA persistido em localStorage
 *   ou IndexedDB (defesa contra XSS). Em reload, hidrata via `/auth/refresh`
 *   que usa o cookie HttpOnly.
 * - `currentUser`: usuário autenticado completo (saldo, handle, ToS).
 * - `isAuthenticated`: derivado — true quando há sessionToken.
 * - `hasAcceptedTos`: derivado — útil pra `tosGuard` (Sprint 1 #5).
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly _sessionToken = signal<string | null>(null);
  private readonly _currentUser = signal<User | null>(null);

  readonly sessionToken = this._sessionToken.asReadonly();
  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this._sessionToken() !== null);
  readonly hasAcceptedTos = computed(() => {
    const u = this._currentUser();
    return u !== null && u.acceptedTosAt !== null && u.acceptedTosAt !== undefined;
  });

  setSession(user: User, sessionToken: string): void {
    this._currentUser.set(user);
    this._sessionToken.set(sessionToken);
  }

  setSessionToken(sessionToken: string): void {
    this._sessionToken.set(sessionToken);
  }

  setCurrentUser(user: User | null): void {
    this._currentUser.set(user);
  }

  clear(): void {
    this._sessionToken.set(null);
    this._currentUser.set(null);
  }
}
